import AsyncStorage from '@react-native-async-storage/async-storage';
import { createClient } from '@supabase/supabase-js';
import Constants from 'expo-constants';
import { Platform } from 'react-native';
import 'react-native-url-polyfill/auto';

// On web, Constants.expoConfig.extra is not reliably embedded; use EXPO_PUBLIC_* (inlined at build time).
// On native, use extra from app.config (set via .env / EAS env).
const isWeb = Platform.OS === 'web';
const supabaseUrl =
  (isWeb ? process.env.EXPO_PUBLIC_SUPABASE_URL : (Constants.expoConfig?.extra as { supabaseUrl?: string } | undefined)?.supabaseUrl) ?? '';
const supabaseAnonKey =
  (isWeb ? process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY : (Constants.expoConfig?.extra as { supabaseAnonKey?: string } | undefined)?.supabaseAnonKey) ?? '';

console.log('[Supabase] Initializing client...');
console.log('[Supabase] Platform:', Platform.OS);
console.log('[Supabase] URL configured:', !!supabaseUrl);
console.log('[Supabase] Anon key configured:', !!supabaseAnonKey);

// Create a dummy client if Supabase is not configured to prevent errors
let supabase: ReturnType<typeof createClient>;

// Validate URL format
const isValidUrl = (url: string): boolean => {
  try {
    const urlObj = new URL(url);
    return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
  } catch {
    return false;
  }
};

// Determine storage and auth settings based on platform
const storage = isWeb ? undefined : AsyncStorage; // undefined = use default localStorage on web
const detectSessionInUrl = isWeb; // Enable URL session detection on web for OAuth callbacks

console.log('[Supabase] Storage:', isWeb ? 'localStorage (web)' : 'AsyncStorage (native)');
console.log('[Supabase] detectSessionInUrl:', detectSessionInUrl);

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('[Supabase] ❌ Missing environment variables!');
  console.error('[Supabase] Please add credentials to .env.development or .env.production (or EAS env):');
  console.error('[Supabase] EXPO_PUBLIC_SUPABASE_URL=your_supabase_project_url');
  console.error('[Supabase] EXPO_PUBLIC_SUPABASE_ANON_KEY=your_supabase_anon_key');
  console.error('[Supabase] Get these from: https://app.supabase.com/project/YOUR_PROJECT/settings/api');

  // Create a client with dummy values to prevent initialization errors
  supabase = createClient('https://placeholder.supabase.co', 'placeholder-key', {
    auth: {
      ...(storage && { storage }),
      autoRefreshToken: false,
      persistSession: false,
      detectSessionInUrl: false,
    },
  });
  console.log('[Supabase] Placeholder client created (will fail on API calls)');
} else if (!isValidUrl(supabaseUrl)) {
  console.error('[Supabase] ❌ Invalid Supabase URL format!');
  console.error('[Supabase] Current URL:', supabaseUrl);
  console.error('[Supabase] URL must be a valid HTTP/HTTPS URL like: https://your-project-id.supabase.co');
  console.error('[Supabase] Check .env.development / .env.production and ensure EXPO_PUBLIC_SUPABASE_URL is correct');

  // Create a client with dummy values
  supabase = createClient('https://placeholder.supabase.co', 'placeholder-key', {
    auth: {
      ...(storage && { storage }),
      autoRefreshToken: false,
      persistSession: false,
      detectSessionInUrl: false,
    },
  });
  console.log('[Supabase] Placeholder client created (will fail on API calls)');
} else {
  console.log('[Supabase] ✅ Creating client with valid credentials...');
  try {
    supabase = createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        ...(storage && { storage }),
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl,
      },
    });
    console.log('[Supabase] ✅ Client created successfully');
  } catch (error) {
    console.error('[Supabase] ❌ Error creating client:', error);
    throw error;
  }
}

export { supabase };
